import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  HostBinding,
  inject,
  Injector,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';

import {
  ButtonModule,
  DatePickerModule,
  EmptyComponent,
  equals,
  IconModule,
  MultiSelectModule,
  PeriodCalendarComponent,
  SelectModule,
  SimpleInputModule,
  SortComponent,
  SortDirective,
} from '@hglobal/fin-ui';
import { FormControl } from '@ngneat/reactive-forms';
import { TranslateModule } from '@ngx-translate/core';
import { asyncScheduler, filter, interval } from 'rxjs';

import { CommentDialog, MonthlyReportDialog, TransactionComponent, TransactionDetailsDialog } from '@kr/components/transactions/components';
import { environment } from '@kr/environment';
import { Transaction, TransactionsPageType, Wallet } from '@kr/models';
import { RouteFiltersService } from '@kr/services';
import { DictionariesStore } from '@kr/store';

import { SearchSelectComponent } from '..';
import { TRANSACTIONS_API, TransactionsApiService } from './api';
import { FileDownloadService } from './file-download.service';
import { TransactionsMockApiService } from './mocks';
import { adaptDateRangeForTransactionsApi, TransactionsFilters } from './models';
import { DIALOG_COLUMNS, PAGE_COLUMNS } from './transactions-columns';
import { initialFilters, TransactionsStore } from './transactions.store';

const REFRESH_TIME = 30 * 1000;

@Component({
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    ButtonModule,
    SelectModule,
    MatPaginatorModule,
    EmptyComponent,
    DatePickerModule,
    SortComponent,
    SortDirective,
    IconModule,
    PeriodCalendarComponent,
    SimpleInputModule,
    MultiSelectModule,
    SearchSelectComponent,
    TransactionComponent,
  ],
  providers: [
    TransactionDetailsDialog,
    CommentDialog,
    FileDownloadService,
    MonthlyReportDialog,
    RouteFiltersService,
    {
      provide: TRANSACTIONS_API,
      useClass: environment.useMocks ? TransactionsMockApiService : TransactionsApiService,
    },
    TransactionsStore,
  ],
  selector: 'kr-transactions',
  templateUrl: './transactions.component.html',
  styleUrls: ['./transactions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TransactionsComponent implements OnInit {
  readonly #store = inject(TransactionsStore);
  readonly #dictionariesStore = inject(DictionariesStore);
  readonly #transactionDetailsDialog = inject(TransactionDetailsDialog);
  readonly #monthlyReportDialog = inject(MonthlyReportDialog);
  readonly #fileDownloadService = inject(FileDownloadService);
  readonly #routeFiltersService = inject(RouteFiltersService<TransactionsFilters>);
  readonly #destroyRef = inject(DestroyRef);
  readonly #injector = inject(Injector);

  readonly wallet = input<Wallet>();
  readonly wallets = input<Wallet[]>();
  readonly pageType = input<TransactionsPageType>();
  readonly isolated = input<boolean>(false);
  readonly providerId = input<number>();

  readonly transactions = this.#store.transactions;
  readonly itemsCount = this.#store.itemsCount;
  readonly isLoading = this.#store.isLoading;
  readonly filters = this.#store.filters;

  readonly transactionStatuses = this.#dictionariesStore.transactionStatuses;
  readonly transactionTypes = this.#dictionariesStore.transactionTypes;
  readonly coins = this.#dictionariesStore.coins;
  readonly providerTypes = this.#dictionariesStore.providerTypes;

  readonly areFiltersShown = signal(true);

  readonly filtersForm = this.#routeFiltersService.initialize({
    filtersState: this.#store.filters,
    updateFilters: (filters: Partial<TransactionsFilters>) => this.#store.updateFilters(filters),
    queryParamTypes: [],
    useCustomControlsLogic: true,
  });

  columnNames: string[];
  sortColumnIndex: Record<string, boolean> = {
    coinId: true,
    created: true,
    amount: true,
    statusOriginal: true,
  };
  dateRangeControl = new FormControl<number[]>([this.filters().createdGte, this.filters().createdLt]);

  readonly getTransactionsEffect = effect(() => {
    const providers = this.#dictionariesStore.providersIds().length;
    const isSuccess = this.#store.isGetTransactionsSuccess();
    if (providers && !isSuccess && !this.isLoading()) {
      this.applyFilters();
    }
  });

  readonly filteredWallets = computed(() => {
    const filters = this.filters();
    const wallets = this.wallets();

    const filteredWallets = filters.providerId ? wallets.filter(wallet => wallet.organizationId === filters.providerId) : wallets;

    return filteredWallets.map(wallet => ({
      ...wallet,
      icon: wallet.organizationType?.toLowerCase() || '',
    }));
  });

  @HostBinding('class.kr-transactions_isolated') get isolatedClass() {
    return !!this.isolated();
  }

  get disabledReset(): boolean {
    return equals(initialFilters, this.filtersForm.value);
  }

  get operationControl(): FormControl<string | null> {
    return this.filtersForm.get('operation') as FormControl<string | null>;
  }

  get statusControl(): FormControl<string | null> {
    return this.filtersForm.get('status') as FormControl<string | null>;
  }

  get providerIdControl(): FormControl<number | null> {
    return this.filtersForm.get('providerId') as FormControl<number | null>;
  }

  get walletIdControl(): FormControl<string | null> {
    return this.filtersForm.get('walletId') as FormControl<string | null>;
  }

  ngOnInit(): void {
    this.#initColumnNames();
    this.#setupSubscriptions();
  }

  changePage(event: PageEvent): void {
    this.#routeFiltersService.updateMatPageControls(event);
    this.applyFilters(false);
  }

  applyFilters(paginationReset = true): void {
    this.#markFormAsUntouched();

    if (paginationReset) {
      this.filtersForm.controls['page'].setValue(1);
    }

    if (this.isolated()) {
      this.#store.getTransactions({
        params: {
          ...adaptDateRangeForTransactionsApi(this.filtersForm.getRawValue()),
          walletId: this.wallet().id,
          providerId: this.providerId(),
        },
      });
    } else {
      this.#store.getTransactions({
        params: {
          ...adaptDateRangeForTransactionsApi(this.filtersForm.getRawValue()),
        },
        once: true,
      });
    }
  }

  resetFilters(): void {
    this.#markFormAsUntouched();
    this.filtersForm.patchValue({ ...initialFilters });
    this.dateRangeControl.patchValue([null, null]);
    asyncScheduler.schedule(() => this.applyFilters());
  }

  openTransactionDetailsDialog(transaction: Transaction): void {
    this.#transactionDetailsDialog.open(transaction);
  }

  toggleFilters(): void {
    this.areFiltersShown.set(!this.areFiltersShown());
  }

  saveOperations(url: string): void {
    this.#fileDownloadService.download(url, this.filtersForm.getRawValue());
  }

  openMonthlyReportDialog(url: string): void {
    this.#monthlyReportDialog
      .open(this.#injector)
      .afterClosed<TransactionsFilters>()
      .pipe(filter(payload => !!payload))
      .subscribe(payload => this.#fileDownloadService.download(url, payload));
  }

  sortChange(): void {
    asyncScheduler.schedule(() => this.applyFilters());
  }

  #initColumnNames(): void {
    this.columnNames = this.isolated() ? DIALOG_COLUMNS : PAGE_COLUMNS;
  }

  #setupSubscriptions(): void {
    if (this.isolated()) {
      this.dateRangeControl.valueChanges
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe(() => asyncScheduler.schedule(() => this.applyFilters()));
      this.filtersForm.controls['comment'].valueChanges
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe(() => asyncScheduler.schedule(() => this.applyFilters()));
      this.filtersForm.controls['status'].valueChanges
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe(() => asyncScheduler.schedule(() => this.applyFilters()));
      this.filtersForm.controls['operation'].valueChanges
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe(() => asyncScheduler.schedule(() => this.applyFilters()));
    }

    this.#subscribeToDateRangeChanges();
    this.#subscribeToProviderIdControl();
    this.#subscribeOnRefresh();
  }

  #subscribeOnRefresh(): void {
    interval(REFRESH_TIME)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => this.applyFilters(false));
  }

  #subscribeToDateRangeChanges(): void {
    this.dateRangeControl.valueChanges.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(value => {
      const [createdGte, createdLt] = value;

      this.filtersForm.patchValue({ createdGte, createdLt });
    });
  }

  #subscribeToProviderIdControl(): void {
    this.filtersForm.controls['providerId'].valueChanges.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => {
      this.filtersForm.patchValue({ walletId: null, coinId: null });
    });
  }

  #markFormAsUntouched(): void {
    this.dateRangeControl.markAsUntouched();
    this.filtersForm.markAsUntouched();
  }
}
